import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import {
  AlertCircle,
  Check,
  CircleArrowLeft,
  Copy,
  Key,
  Link2,
  Loader2,
  Tags,
  Trash2,
  Webhook,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  useCreateDrtIntegration,
  useDeleteDrtIntegration,
  useGetDrtIntegrationsByStudio,
} from '~entities/drt-integration';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';
import { UserRoles } from '~entities/users';

import { Alert, AlertDescription, AlertTitle } from '~shared/shadcn/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { checkMinRoleValue } from '~shared/utils/role-guard';

// DRT Buy Tickets link format per DRT API docs
// Generic: https://integrator.drttix.com/[slug]/[account-id]
// Private (with family): https://integrator.drttix.com/[slug]/[account-id]/[customer-id]
const DRT_INTEGRATOR_BASE = 'https://integrator.drttix.com';
const DRT_SLUG =
  import.meta.env.VITE_DRT_INTEGRATOR_SLUG ?? 'enrollio';

const buildBuyTicketsUrl = (
  accountId: string,
  customerId?: string,
): string => {
  const path = [DRT_SLUG, accountId];
  if (customerId) {
    path.push(customerId);
  }
  return `${DRT_INTEGRATOR_BASE}/${path.join('/')}`;
};

const PendingComponent = () => {
  return (
    <div className='flex h-screen items-center justify-center'>
      <Loader2 className='size-10 animate-spin' />
    </div>
  );
};

const DrtIntegrationPage = () => {
  const { data: studioDetails } = useGetStudioDetails();
  const { data: integrations = [], isLoading } = useGetDrtIntegrationsByStudio(
    studioDetails?._id ?? '',
  );
  const createMutation = useCreateDrtIntegration();
  const deleteMutation = useDeleteDrtIntegration();

  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [drtAccountId, setDrtAccountId] = useState('');

  const activeIntegration = integrations.find((i) => i.isActive);

  const handleGenerateIntegration = async () => {
    if (!studioDetails?._id) {
      toast.error('Studio details not available');
      return;
    }
    const accountId = drtAccountId.trim();
    if (!accountId) {
      toast.error('Please enter your DRT Account ID');
      return;
    }

    try {
      await createMutation.mutateAsync({
        studioId: studioDetails._id,
        accountId,
      });
      setDrtAccountId('');
      toast.success('DRT Integration credentials generated successfully!');
    } catch (error) {
      console.error('Failed to generate DRT integration.', error);
    }
  };

  const handleCopy = async (text: string, field: string) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        setCopiedField(field);
        toast.success(`${field} copied to clipboard!`);
        setTimeout(() => {
          setCopiedField(null);
        }, 2000);
        return;
      }
    } catch (error) {
      console.log('Modern clipboard API failed, trying fallback:', error);
    }

    try {
      // Fallback 1: execCommand method
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      textArea.remove();

      if (successful) {
        setCopiedField(field);
        toast.success(`${field} copied to clipboard!`);
        setTimeout(() => {
          setCopiedField(null);
        }, 2000);
        return;
      }
    } catch (error) {
      console.log('execCommand failed, trying selection method:', error);
    }

    // Fallback 2: Select the actual input field for user to manually copy
    try {
      const inputElement = document.querySelector(
        `input[value="${text}"]`
      ) as HTMLInputElement;

      if (inputElement) {
        inputElement.focus();
        inputElement.select();
        toast.info(`Text selected! Press Ctrl+C (or Cmd+C) to copy`, {
          duration: 4000,
        });
        setCopiedField(field);
        setTimeout(() => {
          setCopiedField(null);
        }, 4000);
        return;
      }
    } catch (error) {
      console.error('Selection method failed:', error);
    }

    // Final fallback: Show a modal with the text
    toast.error(
      'Auto-copy not available. Please manually select and copy the text.',
      { duration: 5000 }
    );
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('DRT Integration deleted successfully!');
    } catch {
      toast.error('Failed to delete DRT integration');
    }
  };

  if (isLoading) {
    return (
      <div className='flex h-96 items-center justify-center'>
        <Loader2 className='size-10 animate-spin' />
      </div>
    );
  }

  return (
    <div className='container mx-auto max-w-6xl py-8'>
      <Link to='/dashboard' className='mb-8 flex items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <span className='text-lg font-semibold'>Back to Dashboard</span>
      </Link>

      <div className='container relative max-w-6xl'>
        <div className='space-y-8'>
          <div>
            <h1 className='text-3xl font-bold'>DRT Integration</h1>
            <p className='mt-2 text-muted-foreground'>
              Connect your Enrollio studio with DRT for seamless ticketing and
              event management. Go to Enrollio Studio Portal &gt; DRT Integration
              and follow the instructions below to connect your account.
            </p>
          </div>

          {/* Connect Your Account + Integration Benefits */}
          <Alert>
            <AlertCircle className='size-4' />
            <AlertTitle>Connect Your Account &ndash; Integration Benefits</AlertTitle>
            <AlertDescription>
              <ul className='mt-2 list-disc space-y-1 pl-5'>
                <li>Reuse configuration information from Enrollio</li>
                <li>Authoritative identification of shoppers</li>
                <li>Consolidated financial information</li>
                <li>Automatic buyer details pre-filling</li>
              </ul>
            </AlertDescription>
          </Alert>

          <div className='grid gap-8 lg:grid-cols-2'>
            {/* Generate/Display Integration */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {activeIntegration
                    ? 'Integration Credentials'
                    : 'Generate Integration'}
                </CardTitle>
                <CardDescription>
                  {activeIntegration
                    ? 'Your DRT integration credentials - share these with DRT'
                    : 'Click the button below to generate your DRT integration credentials'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {activeIntegration ? (
                  <div className='space-y-4'>
                    <div className='space-y-2'>
                      <Label>Integration Key</Label>
                      <div className='flex gap-2'>
                        <Input
                          value={activeIntegration.integrationKey}
                          readOnly
                          className='font-mono text-sm'
                        />
                        <Button
                          variant='outline'
                          size='icon'
                          onClick={() =>
                            handleCopy(
                              activeIntegration.integrationKey,
                              'Integration Key',
                            )
                          }
                        >
                          {copiedField === 'Integration Key' ? (
                            <Check className='size-4' />
                          ) : (
                            <Copy className='size-4' />
                          )}
                        </Button>
                      </div>
                      <p className='text-xs text-muted-foreground'>
                        This is the unique key that DRT will use to authenticate
                        with Enrollio
                      </p>
                    </div>

                    <div className='space-y-2'>
                      <Label>Integrator Studio ID</Label>
                      <div className='flex gap-2'>
                        <Input
                          value={activeIntegration.integratorStudioId}
                          readOnly
                          className='font-mono text-sm'
                        />
                        <Button
                          variant='outline'
                          size='icon'
                          onClick={() =>
                            handleCopy(
                              activeIntegration.integratorStudioId,
                              'Integrator Studio ID',
                            )
                          }
                        >
                          {copiedField === 'Integrator Studio ID' ? (
                            <Check className='size-4' />
                          ) : (
                            <Copy className='size-4' />
                          )}
                        </Button>
                      </div>
                      <p className='text-xs text-muted-foreground'>
                        Studio identifier used for DRT token authentication
                      </p>
                    </div>

                    <div className='space-y-2'>
                      <Label>Account ID</Label>
                      <div className='flex gap-2'>
                        <Input
                          value={activeIntegration.accountId}
                          readOnly
                          className='font-mono text-sm'
                        />
                        <Button
                          variant='outline'
                          size='icon'
                          onClick={() =>
                            handleCopy(
                              activeIntegration.accountId,
                              'Account ID',
                            )
                          }
                        >
                          {copiedField === 'Account ID' ? (
                            <Check className='size-4' />
                          ) : (
                            <Copy className='size-4' />
                          )}
                        </Button>
                      </div>
                      <p className='text-xs text-muted-foreground'>
                        DRT account ID used in ticketing URLs (same as in DRT integration)
                      </p>
                    </div>

                    <div className='space-y-2'>
                      <Label>Account ID</Label>
                      <div className='flex gap-2'>
                        <Input
                          value={activeIntegration.accountId}
                          readOnly
                          className='font-mono text-sm'
                        />
                        <Button
                          variant='outline'
                          size='icon'
                          onClick={() =>
                            handleCopy(
                              activeIntegration.accountId,
                              'Account ID',
                            )
                          }
                        >
                          {copiedField === 'Account ID' ? (
                            <Check className='size-4' />
                          ) : (
                            <Copy className='size-4' />
                          )}
                        </Button>
                      </div>
                      <p className='text-xs text-muted-foreground'>
                        DRT account ID used in ticketing URLs (same as in DRT integration)
                      </p>
                    </div>

                    <div className='pt-4'>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant='destructive' className='w-full'>
                            <Trash2 className='mr-2 size-4' />
                            Delete Integration
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete your DRT integration.
                              You will need to reconfigure DRT with new
                              credentials if you generate a new integration.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() =>
                                handleDelete(activeIntegration._id)
                              }
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ) : (
                  <div className='space-y-4'>
                    <Alert>
                      <Key className='size-4' />
                      <AlertTitle>How it works</AlertTitle>
                      <AlertDescription>
                        Enter the Account ID that DRT provided for your account
                        (find it in your DRT Admin Portal, e.g. Client Tools &gt;
                        Integrate with Studio Management Platform). Enrollio will
                        then generate an Integration Key. The same Account ID is
                        used in your ticketing links.
                      </AlertDescription>
                    </Alert>

                    <div className='space-y-2'>
                      <Label htmlFor='drt-account-id'>
                        DRT Account ID <span className='text-destructive'>*</span>
                      </Label>
                      <Input
                        id='drt-account-id'
                        placeholder='e.g. 69ab32ad041596b815dad99a'
                        value={drtAccountId}
                        onChange={(e) => setDrtAccountId(e.target.value)}
                        className='font-mono text-sm'
                      />
                      <p className='text-xs text-muted-foreground'>
                        Same ID you use when connecting in DRT; it will appear in
                        ticketing URLs (e.g. https://integrator.drttix.com/enrollio/[this-id])
                      </p>
                    </div>

                    <Button
                      onClick={() => void handleGenerateIntegration()}
                      className='w-full'
                      size='lg'
                      disabled={createMutation.isPending || !drtAccountId.trim()}
                    >
                      {createMutation.isPending ? (
                        <>
                          <Loader2 className='mr-2 size-5 animate-spin' />
                          <span className='mr-2'>Generating...</span>
                        </>
                      ) : (
                        <>
                          <Key className='mr-2 size-5' />
                          Generate DRT Integration Credentials
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Setup Instructions */}
            <Card>
              <CardHeader>
                <CardTitle>Setup Instructions</CardTitle>
                <CardDescription>
                  Follow these steps to configure DRT integration
                </CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='space-y-3'>
                  <div className='flex gap-3'>
                    <div className='flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground'>
                      1
                    </div>
                    <div>
                      <h4 className='font-medium'>
                        {activeIntegration
                          ? 'Copy Your Credentials'
                          : 'Generate Integration Credentials'}
                      </h4>
                      <p className='text-sm text-muted-foreground'>
                        {activeIntegration
                          ? 'Copy the Integration Key, Integrator Studio ID, and Account ID from the panel on the left.'
                          : 'Enter your DRT Account ID and click "Generate DRT Integration Credentials".'}
                      </p>
                    </div>
                  </div>

                  <div className='flex gap-3'>
                    <div className='flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground'>
                      2
                    </div>
                    <div>
                      <h4 className='font-medium'>
                        Provide Credentials to DRT
                      </h4>
                      <p className='text-sm text-muted-foreground'>
                        {`Log into your DRT Admin Portal and go to Client Tools >
                        Integrate with Studio Management Platform, then paste your
                        Integration Key and Account ID (same ID you entered above) and click "Connect my account".`}
                      </p>
                    </div>
                  </div>

                  <div className='flex gap-3'>
                    <div className='flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground'>
                      3
                    </div>
                    <div>
                      <h4 className='font-medium'>
                        Import Tags and Families
                      </h4>
                      <p className='text-sm text-muted-foreground'>
                        Once your DRT account is connected, you can acccess
                        tags and familied that you have set up with us directly in your DRT Admin Portal.
                      </p>
                    </div>
                  </div>

                  <div className='flex gap-3'>
                    <div className='flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground'>
                      4
                    </div>
                    <div>
                      <h4 className='font-medium'>Pre Authenticated Buy Tickets Link</h4>
                      <p className='text-sm text-muted-foreground'>
                        {`Once your DRT account is connected, you can share a "Buy Tickets"
                        link with one of your studio families that has an account. 
                        When they access the site through that link, their details, coupons, 
                        priority codes, and even performer will load automatically!`}
                      </p>
                    </div>
                  </div>
                </div>

                <div className='border-t pt-4'>
                  <h4 className='mb-2 font-medium'>Testing Resources</h4>
                  <a
                    href='https://sandbox.drttix.com/integrator-test/config.cfm'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='flex items-center gap-1 text-sm text-primary hover:underline'
                  >
                    DRT Integrator Test Configuration Page
                    <svg
                      className='size-4'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                      />
                    </svg>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Import Tags and Families to DRT */}
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <Tags className='size-5' />
                Import Tags and Families to DRT
              </CardTitle>
              <CardDescription>
                Share your Enrollio tags and family data with DRT for marketing
                and ticketing campaigns
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className='text-sm text-muted-foreground'>
                Once your DRT account is connected, you can access the tags and
                families you have set up in Enrollio directly from your DRT
                Admin Portal. DRT uses your Contact tags (the tags assigned to
                families/parents) and their email addresses for segmentation and
                email campaigns.
              </p>
            </CardContent>
          </Card>

          {/* Pre-authenticated Buy Tickets Link */}
          {activeIntegration && activeIntegration.accountId && (
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Link2 className='size-5' />
                  Buy Tickets Link
                </CardTitle>
                <CardDescription>
                  Share a pre-authenticated link with your studio families. When
                  they open it, their details, coupons, priority codes, and
                  performer info load automatically. DRT resolves buyer identity
                  via the /buyer-details endpoint for private links.
                </CardDescription>
              </CardHeader>
              <CardContent className='space-y-6'>
                <div className='space-y-2'>
                  <Label>Link 1: Generic (send to anyone)</Label>
                  <div className='flex gap-2'>
                    <Input
                      value={buildBuyTicketsUrl(activeIntegration.accountId)}
                      readOnly
                      className='font-mono text-sm'
                    />
                    <Button
                      variant='outline'
                      size='icon'
                      onClick={() =>
                        handleCopy(
                          buildBuyTicketsUrl(activeIntegration.accountId),
                          'Generic Buy Tickets Link',
                        )
                      }
                    >
                      {copiedField === 'Generic Buy Tickets Link' ? (
                        <Check className='size-4' />
                      ) : (
                        <Copy className='size-4' />
                      )}
                    </Button>
                  </div>
                  <p className='text-xs text-muted-foreground'>
                    Generic link (no family): DRT won&apos;t know buyer identity
                    until they enter details.
                  </p>
                </div>
                <div className='space-y-2'>
                  <Label>Link 2: Private (per-family, includes family ID)</Label>
                  <p className='text-xs text-muted-foreground'>
                    Append the family&apos;s parent ID to the generic link:{' '}
                    <code className='rounded bg-muted px-1'>
                      {buildBuyTicketsUrl(activeIntegration.accountId)}/[parent-id]
                    </code>
                    . Use the parent ID from your family list. Note: if someone
                    visits the generic link first, they cannot then use the
                    private link for that session.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Using Ticket Order Webhooks */}
          {activeIntegration && (
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <Webhook className='size-5' />
                  Using Ticket Order Webhooks
                </CardTitle>
                <CardDescription>
                  When DRT sends ticket order data to Enrollio, you can trigger
                  GHL workflows for reminders, sequences, and lead enrichment
                </CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                <p className='text-sm text-muted-foreground'>
                  DRT sends completed order details to Enrollio via the Order
                  Manifest webhook. Enrollio then fires a GHL trigger with the
                  order data (buyer info, order total, shows, seats). You can set
                  up a workflow in GoHighLevel that listens for this trigger to:
                </p>
                <ul className='list-disc space-y-1 pl-5 text-sm text-muted-foreground'>
                  <li>Send reminder emails before the show</li>
                  <li>Start automated sequences for ticket buyers</li>
                  <li>Populate leads for new buyers who aren&apos;t yet in your portal</li>
                  <li>Add tags or update contact fields based on purchase</li>
                </ul>
                <p className='text-sm text-muted-foreground'>
                  Configure your GHL workflow to use the trigger key{' '}
                  <code className='rounded bg-muted px-1'>
                    DRT_TICKET_ORDER
                  </code>{' '}
                  (or the value of{' '}
                  <code className='rounded bg-muted px-1'>
                    DRT_TICKET_ORDER_TRIGGER_KEY
                  </code>{' '}
                  if set in your environment). The trigger payload includes
                  buyer details, order total, and show information.
                </p>
              </CardContent>
            </Card>
          )}

        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/_auth/_layout/drt-integration')({
  beforeLoad: async () => {
    const hasAccess = await checkMinRoleValue(UserRoles.SUPER_ADMIN);
    if (!hasAccess) {
      return redirect({
        to: '/dashboard',
      });
    }
  },
  pendingComponent: PendingComponent,
  component: DrtIntegrationPage,
});
